import React, { useEffect, useState } from "react";
import { Building2, Pill, Stethoscope } from "lucide-react";
import api from "../lib/api";
import LandingView from "./LandingView";
import { discoveryLink } from "../lib/discoverySearch";

const categories = [
  { key: "doctors", title: "Doctors", icon: Stethoscope, description: "Find a doctor for your care", color: "bg-blue-50 text-blue-700" },
  { key: "clinics", title: "Clinics", icon: Building2, description: "Explore clinics and book a visit", color: "bg-cyan-50 text-cyan-700" },
  { key: "pharmacies", title: "Pharmacies", icon: Pill, description: "Visit registered pharmacy stores", color: "bg-emerald-50 text-emerald-700" },
];
const initialResults = () => Object.fromEntries(categories.map(({ key }) => [key, { loading: true, items: [], error: false }]));

export default function Landing() {
  const [input, setInput] = useState("");
  const [locationInput, setLocationInput] = useState("");
  const [search, setSearch] = useState({ q: "", location: "", specialization: "", department: "", service: "all" });
  const [careType, setCareType] = useState("all");
  const [filterOptions, setFilterOptions] = useState({ specialties: [], departments: [] });
  useEffect(() => { let active = true; for (const [key,url] of [["specialties","/public/directory/specialties"],["departments","/public/directory/departments"]]) api.get(url).then(r => { if(active) setFilterOptions(old => ({...old,[key]: (r.data.items || []).filter(x => typeof x === "string")})); }).catch(() => {}); return () => {active=false}; }, []);
  const [coords, setCoords] = useState(null);
  const [locating, setLocating] = useState(false);
  const [locationError, setLocationError] = useState("");
  const [results, setResults] = useState(initialResults);
  const [retry, setRetry] = useState(0);
  useEffect(() => {
    let active = true;
    setResults(initialResults());
    const common = { q: search.q, limit: 3, ...(coords || {}) };
    const requests = {
      doctors: ["/public/directory/doctors", { ...common, location: search.location, specialization: search.specialization }],
      clinics: ["/public/directory/clinics", { ...common, location: search.location, interest: search.specialization, department: search.department }],
      pharmacies: ["/v1/public/pharmacies", { ...common, city: search.location, service: search.service || "all" }],
    };
    for (const [key, [url, params]] of Object.entries(requests)) {
      if (careType !== "all" && key !== careType) continue;
      api.get(url, { params }).then(r => { if (active) setResults(previous => ({ ...previous, [key]: { loading: false, items: r.data.items || [], total: r.data.total ?? (r.data.items || []).length, error: false } })); })
        .catch(() => { if (active) setResults(previous => ({ ...previous, [key]: { loading: false, items: [], error: true } })); });
    }
    return () => { active = false; };
  }, [search, coords, retry, careType]);
  const locate = () => {
    setLocationError("");
    if (!navigator.geolocation) { setLocationError("Location is unavailable. Enter your city, area or postcode instead."); return; }
    setLocating(true);
    navigator.geolocation.getCurrentPosition(({ coords: position }) => {
      setCoords({ latitude: position.latitude, longitude: position.longitude });
      setLocationInput(""); setSearch(current => ({ ...current, location: "" })); setLocating(false);
    }, () => { setLocationError("Location access is unavailable. Enter your city, area or postcode instead."); setLocating(false); }, { timeout: 10000 });
  };
  const link = key => discoveryLink(key, { ...search, ...(coords || {}) });
  const filtered = search.q || search.location || search.specialization || search.department || (search.service && search.service !== "all");
  return <LandingView careType={careType} setCareType={setCareType} filterOptions={filterOptions} categories={categories} input={input} setInput={setInput} locationInput={locationInput} setLocationInput={setLocationInput} search={search} setSearch={setSearch} coords={coords} setCoords={setCoords} locating={locating} locate={locate} locationError={locationError} setLocationError={setLocationError} results={results} retry={() => setRetry(value => value + 1)} link={link} filtered={filtered} />;
}
